import React, { useContext, useState } from "react";
import { injectIntl } from "react-intl";
import { Badge, Button, Progress } from "reactstrap";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import "./ShopeeOverview.scss";

const money = (value) => Number(value || 0).toLocaleString("vi-VN");

const WEBHOOK_STATUS = {
  configured: {
    color: "success",
    labelId: "shopee.workspace.overview.webhook.configured",
  },
  processing: {
    color: "warning",
    labelId: "shopee.workspace.overview.webhook.processing",
  },
  degraded: {
    color: "danger",
    labelId: "shopee.workspace.overview.webhook.degraded",
  },
  not_configured: {
    color: "secondary",
    labelId: "shopee.workspace.overview.webhook.not-configured",
  },
  unknown: {
    color: "secondary",
    labelId: "shopee.workspace.overview.webhook.unknown",
  },
};

const PanelHeading = ({ children, action, onAction }) => (
  <div className="shopee-overview__panel-heading">
    <h3>{children}</h3>
    {action ? (
      <button type="button" onClick={onAction} className="shopee-overview__panel-action">
        {action} <i className="simple-icon-arrow-right ml-1" />
      </button>
    ) : null}
  </div>
);

// Micro SVG Sparklines for KPI Cards
const KpiSparkline = ({ color = "#EE4D2D" }) => (
  <svg
    viewBox="0 0 100 30"
    className="shopee-overview__kpi-sparkline"
    aria-hidden="true"
  >
    <path
      d="M0,25 Q15,10 30,18 T60,8 T90,15 T100,5"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M0,25 Q15,10 30,18 T60,8 T90,15 T100,5 L100,30 L0,30 Z"
      fill={color}
      opacity="0.1"
    />
  </svg>
);

export const getTopSellingProducts = (mappings) =>
  (Array.isArray(mappings) ? mappings : [])
    .map((product) => ({ product, sold: Number(product.sold_count) }))
    .filter(({ sold }) => Number.isFinite(sold) && sold > 0)
    .sort((left, right) => right.sold - left.sold)
    .slice(0, 3);

export default injectIntl(function ShopeeOverviewPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const [period, setPeriod] = useState("today");
  const { data } = workspace;
  const summary = data.summary || {};
  const sales = summary.sales || {};
  const operations = summary.operations || {};
  const mappings = data.mappings || [];
  const issues = data.issues || {};
  const issueOrders = issues.orders || [];
  const issueMappings = issues.mappings || [];
  const issueJobs = issues.jobs || [];
  const health = data.health || {};
  const healthShops = health.shops || [];
  const webhook = health.webhook || {};
  const webhookState =
    WEBHOOK_STATUS[String(webhook.status || "unknown")] || WEBHOOK_STATUS.unknown;
  const totalSKUs = mappings.length;
  const mappedSKUs = mappings.filter((mapping) => mapping.product_id).length;
  const percentMapped = totalSKUs
    ? Math.round((mappedSKUs / totalSKUs) * 100)
    : 0;
  const gmv = Number(sales.gmv || 0);
  const newOrders = Number(sales.new_orders || operations.orders || 0);
  const pendingOrders = Number(
    sales.pending_orders || operations.ready_to_ship || 0,
  );
  const expiringOrders = Number(sales.expiring_orders || 0);
  const isHealthy = String(health.status || "healthy") === "healthy";
  const failedJobs = Number(health.failed_jobs || issueJobs.length || 0);
  const oauthValid = healthShops.length
    ? healthShops.every((shop) => shop.token_health !== "expired")
    : isHealthy;

  const kpiCards = [
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.new-orders",
      }),
      value: newOrders,
      icon: "iconsminds-shopping-cart",
      tone: "blue",
      trend: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.new-trend",
      }),
      trendTone: "positive",
      comparison: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.new-comparison",
      }),
      sparklineColor: "#2563eb",
    },
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.pending",
      }),
      value: pendingOrders,
      icon: "iconsminds-box-close",
      tone: "orange",
      trend: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.pending-trend",
      }),
      trendTone: pendingOrders > 0 ? "warning" : "positive",
      comparison: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.pending-comparison",
      }),
      sparklineColor: "#f59e0b",
    },
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.expiring",
      }),
      value: expiringOrders,
      icon: "iconsminds-clock",
      tone: "purple",
      trend: intl.formatMessage({
        id:
          expiringOrders > 0
            ? "shopee.workspace.overview.kpi.expiring-priority"
            : "shopee.workspace.overview.kpi.expiring-safe",
      }),
      trendTone: expiringOrders > 0 ? "danger" : "positive",
      comparison: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.expiring-comparison",
      }),
      sparklineColor: "#8b5cf6",
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.overview.kpi.gmv" }),
      value: `${money(gmv)} đ`,
      icon: "iconsminds-money-bag",
      tone: "green",
      trend: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.gmv-trend",
      }),
      trendTone: "positive",
      comparison: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.gmv-comparison",
      }),
      sparklineColor: "#10b981",
    },
    {
      label: intl.formatMessage({ id: "shopee.workspace.overview.kpi.ontime" }),
      value: "100%",
      icon: "simple-icon-target",
      tone: "cyan",
      trend: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.ontime-trend",
      }),
      trendTone: "positive",
      comparison: intl.formatMessage({
        id: "shopee.workspace.overview.kpi.ontime-comparison",
      }),
      sparklineColor: "#06b6d4",
    },
  ];

  const issueItems = [
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.issue.orders",
      }),
      description: intl.formatMessage({
        id: "shopee.workspace.overview.issue.orders-desc",
      }),
      count: issueOrders.length || pendingOrders,
      icon: "iconsminds-box-close",
      tone: "orange",
      actionKey: "orders",
    },
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.issue.mappings",
      }),
      description: intl.formatMessage({
        id: "shopee.workspace.overview.issue.mappings-desc",
      }),
      count: issueMappings.length,
      icon: "iconsminds-synchronize",
      tone: "purple",
      actionKey: "mappings",
    },
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.issue.jobs",
      }),
      description: intl.formatMessage({
        id: "shopee.workspace.overview.issue.jobs-desc",
      }),
      count: failedJobs,
      icon: "simple-icon-refresh",
      tone: "red",
      actionKey: "issues",
    },
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.issue.products",
      }),
      description: intl.formatMessage({
        id: "shopee.workspace.overview.issue.products-desc",
      }),
      count: mappings.filter((mapping) => !mapping.product_id).length,
      icon: "iconsminds-box-full",
      tone: "blue",
      actionKey: "products",
    },
    {
      label: intl.formatMessage({
        id: "shopee.workspace.overview.issue.reconciliation",
      }),
      description: intl.formatMessage({
        id: "shopee.workspace.overview.issue.reconciliation-desc",
      }),
      count: Number(operations.issues || 0),
      icon: "iconsminds-money-bag",
      tone: "gray",
      actionKey: "reconciliation",
    },
  ];

  const salesChannels =
    workspace.shops && workspace.shops.length
      ? workspace.shops.slice(0, 5).map((shop) => ({
          name:
            shop.shop_name ||
            intl.formatMessage(
              { id: "shopee.workspace.overview.shop-fallback" },
              { shopId: shop.shop_id },
            ),
          handle: shop.merchant_name || shop.shop_region || "Shopee",
          orders: newOrders,
          type: "S",
          state:
            shop.authorization_status === "authorized" ? "active" : "error",
        }))
      : [];

  const recentActivity =
    data.jobs && data.jobs.length > 0
      ? data.jobs.slice(0, 5).map((job) => ({
          label: `${job.action || intl.formatMessage({ id: "shopee.workspace.overview.activity.sync-default" })} ${job.resource || ""}`,
          detail: "Shopee",
          time: job.createdAt
            ? new Date(job.createdAt).toLocaleString("vi-VN")
            : intl.formatMessage({
                id: "shopee.workspace.overview.activity.just-now",
              }),
          result: /completed/i.test(job.status)
            ? intl.formatMessage({
                id: "shopee.workspace.overview.activity.success",
              })
            : job.status,
          meta:
            job.last_error ||
            intl.formatMessage(
              { id: "shopee.workspace.overview.activity.attempts" },
              { count: job.attempts || 1 },
            ),
          tone: /completed/i.test(job.status)
            ? "success"
            : /failed|dead/i.test(job.status)
              ? "danger"
              : "warning",
        }))
      : [];

  const topProducts = getTopSellingProducts(mappings);
  const isAllShops =
    !workspace.selectedShopId || workspace.selectedShopId === "ALL";
  const revenueChannels =
    workspace.shops && workspace.shops.length
      ? workspace.shops
          .filter(
            (shop) =>
              isAllShops ||
              String(shop.shop_id) === String(workspace.selectedShopId),
          )
          .map((shop, index) => {
            const shopCount = workspace.shops.length;
            const share =
              isAllShops && shopCount > 1 ? Math.round(gmv / shopCount) : gmv;
            const percent =
              isAllShops && shopCount > 1 ? Math.round(100 / shopCount) : 100;
            return {
              label:
                shop.shop_name ||
                intl.formatMessage(
                  { id: "shopee.workspace.overview.shop-label" },
                  { shopId: shop.shop_id },
                ),
              amount: share,
              percent,
              tone: index % 2 === 0 ? "blue" : "orange",
            };
          })
      : [];

  return (
    <div className="shopee-overview">
      {/* Top Header Banner */}
      <div className="shopee-overview__header-banner mb-3 p-3 rounded-lg shadow-sm">
        <div className="d-flex flex-wrap align-items-center justify-content-between">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div className="shopee-overview__banner-icon mr-3">
              <i className="simple-icon-speedometer font-weight-bold" />
            </div>
            <div>
              <div className="d-flex align-items-center">
                <h5 className="mb-0 font-weight-bold text-dark mr-2" style={{ fontSize: "16px" }}>
                  <IntlMessages id="shopee.workspace.overview.banner-title" />
                </h5>
                <Badge color={isHealthy ? "light-success" : "light-danger"} pill className="px-2 py-1" style={{ fontSize: "10px" }}>
                  <span className="live-dot mr-1" />
                  {isHealthy ? (
                    <IntlMessages id="shopee.workspace.overview.banner-healthy" />
                  ) : (
                    <IntlMessages id="shopee.workspace.overview.banner-attention" />
                  )}
                </Badge>
              </div>
              <small className="text-muted">
                <IntlMessages
                  id="shopee.workspace.overview.banner-shops-updated"
                  values={{
                    count: workspace.shops?.length || 0,
                    time: new Date().toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }),
                  }}
                />
              </small>
            </div>
          </div>

          <div className="d-flex align-items-center flex-wrap gap-2">
            <div className="shopee-overview__period-toggle mr-2">
              <button
                type="button"
                className={`period-btn ${period === "today" ? "active" : ""}`}
                onClick={() => setPeriod("today")}
              >
                <IntlMessages id="shopee.workspace.overview.period.today" />
              </button>
              <button
                type="button"
                className={`period-btn ${period === "week" ? "active" : ""}`}
                onClick={() => setPeriod("week")}
              >
                <IntlMessages id="shopee.workspace.overview.period.week" />
              </button>
              <button
                type="button"
                className={`period-btn ${period === "month" ? "active" : ""}`}
                onClick={() => setPeriod("month")}
              >
                <IntlMessages id="shopee.workspace.overview.period.month" />
              </button>
            </div>

            <Button
              color="primary"
              size="sm"
              className="d-flex align-items-center px-3 rounded-pill"
              onClick={workspace.load}
              disabled={workspace.loading}
              style={{ background: "#EE4D2D", border: "none" }}
            >
              <i className={`simple-icon-refresh mr-1 ${workspace.loading ? "spin" : ""}`} />
              {workspace.loading ? (
                <IntlMessages id="shopee.workspace.common.loading" />
              ) : (
                <IntlMessages id="shopee.workspace.overview.reload" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="shopee-overview__kpis">
        {kpiCards.map((kpi) => (
          <section className="shopee-overview__kpi" key={kpi.label}>
            <span className={`shopee-overview__icon shopee-overview__icon--${kpi.tone}`}>
              <i className={kpi.icon} />
            </span>
            <div className="shopee-overview__kpi-content">
              <span className="shopee-overview__label">{kpi.label}</span>
              <div className="shopee-overview__kpi-line">
                <strong>{kpi.value}</strong>
                {kpi.trend && (
                  <span className={`shopee-overview__trend shopee-overview__trend--${kpi.trendTone}`}>
                    {kpi.trend}
                  </span>
                )}
              </div>
              <small>{kpi.comparison}</small>
            </div>
            <KpiSparkline color={kpi.sparklineColor} />
          </section>
        ))}
      </div>

      {/* Summary Grid: Mapping & System Health */}
      <div className="shopee-overview__summary-grid">
        <section className="shopee-overview__panel shopee-overview__mapping">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h3 className="mb-0">
              <IntlMessages id="shopee.workspace.overview.mapping-title" />{" "}
              <i
                className="simple-icon-info text-muted"
                title={intl.formatMessage({
                  id: "shopee.workspace.overview.mapping-tooltip",
                })}
              />
            </h3>
            <Badge color="primary" pill style={{ background: "#EE4D2D", fontSize: "11px" }}>
              <IntlMessages
                id="shopee.workspace.overview.mapping-percent"
                values={{ percent: percentMapped }}
              />
            </Badge>
          </div>

          <div className="shopee-overview__progress-label my-2">
            <span>
              <IntlMessages
                id="shopee.workspace.overview.mapping-progress"
                values={{ mapped: mappedSKUs, total: totalSKUs }}
              />
            </span>
            <strong className="text-primary">{percentMapped}%</strong>
          </div>

          <Progress
            value={percentMapped}
            className="shopee-overview__progress my-2"
            style={{ height: "8px", borderRadius: "10px" }}
          />

          <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top">
            <small className="text-muted">
              <IntlMessages id="shopee.workspace.overview.mapping-note" />
            </small>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary px-3 py-1 rounded-pill"
              onClick={() => workspace.setActiveTab("mappings")}
              style={{ fontSize: "11px", borderColor: "#EE4D2D", color: "#EE4D2D" }}
            >
              <IntlMessages id="shopee.workspace.overview.mapping-action" />{" "}
              <i className="simple-icon-arrow-right ml-1" />
            </button>
          </div>
        </section>

        <section className="shopee-overview__panel shopee-overview__health">
          <PanelHeading>
            <IntlMessages id="shopee.workspace.overview.health-title" />{" "}
            <i className="simple-icon-info text-muted" />
          </PanelHeading>
          <span
            className={`shopee-overview__health-status shopee-overview__status shopee-overview__status--${
              isHealthy ? "success" : "danger"
            }`}
          >
            {isHealthy ? "Healthy" : "Degraded"}
          </span>

          <p className="mb-3">
            <IntlMessages id="shopee.workspace.overview.last-sync" />{" "}
            <strong className="text-dark">
              {recentActivity.length ? recentActivity[0].time : intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" })}
            </strong>{" "}
            <span className="mx-1">|</span> Webhook:{" "}
            <span
              className={`badge badge-${webhookState.color} px-2 py-1`}
              style={{ fontSize: "10px" }}
            >
              <IntlMessages id={webhookState.labelId} />
            </span>
          </p>

          <div className="shopee-overview__health-body">
            <div
              className="shopee-overview__sparkline"
              aria-label={intl.formatMessage({
                id: "shopee.workspace.overview.sparkline-label",
              })}
            >
              <span className="text-muted font-weight-bold">
                <IntlMessages id="shopee.workspace.overview.sparkline-label" />
              </span>
              <svg viewBox="0 0 420 74" role="img" aria-hidden="true">
                <polyline points="0,42 40,42 60,24 82,24 108,54 142,54 162,14 186,34 210,16 252,16 280,42 318,42 340,20 382,28 420,16" />
              </svg>
            </div>

            <div className="shopee-overview__health-checks">
              <div className="d-flex align-items-center p-2 rounded mb-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <span className="shopee-overview__mini-icon shopee-overview__mini-icon--green mr-2">
                  <i className="simple-icon-shield" />
                </span>
                <span style={{ fontSize: "11px" }}>
                  <IntlMessages id="shopee.workspace.overview.oauth-label" />{" "}
                  <strong className={oauthValid ? "text-success" : "text-danger"}>
                    {oauthValid ? (
                      <IntlMessages id="shopee.workspace.overview.oauth-valid" />
                    ) : (
                      <IntlMessages id="shopee.workspace.overview.oauth-invalid" />
                    )}
                  </strong>
                </span>
              </div>
              <div className="d-flex align-items-center p-2 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <span className="shopee-overview__mini-icon shopee-overview__mini-icon--blue mr-2">
                  <i className="simple-icon-chart" />
                </span>
                <span style={{ fontSize: "11px" }}>
                  <IntlMessages id="shopee.workspace.overview.api-label" />{" "}
                  <strong className={isHealthy ? "text-success" : "text-warning"}>
                    {isHealthy ? (
                      <IntlMessages id="shopee.workspace.overview.api-good" />
                    ) : (
                      <IntlMessages id="shopee.workspace.overview.api-bad" />
                    )}
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Operations Grid: Issues, Channels, Recent Activity */}
      <div className="shopee-overview__operations-grid">
        {/* Issue Center Widget */}
        <section className="shopee-overview__panel shopee-overview__issues">
          <PanelHeading
            action={intl.formatMessage({ id: "shopee.workspace.common.view-all" })}
            onAction={() => workspace.setActiveTab("issues")}
          >
            <IntlMessages id="shopee.workspace.overview.issues-title" />{" "}
            <i className="simple-icon-info text-muted" />
          </PanelHeading>
          <div className="shopee-overview__issue-list">
            {issueItems.map((item) => (
              <button
                type="button"
                className="shopee-overview__issue-row transition-all"
                key={item.label}
                onClick={() => workspace.setActiveTab(item.actionKey || "issues")}
              >
                <span className={`shopee-overview__icon shopee-overview__icon--${item.tone}`}>
                  <i className={item.icon} />
                </span>
                <span>
                  <strong>
                    {item.label} {item.count > 0 ? <em>{item.count}</em> : <em style={{ background: "#94a3b8" }}>0</em>}
                  </strong>
                  <small>{item.description}</small>
                </span>
                <i className="simple-icon-arrow-right text-muted" />
              </button>
            ))}
          </div>
        </section>

        {/* Sales Channels Widget */}
        <section className="shopee-overview__panel shopee-overview__channels">
          <PanelHeading
            action={intl.formatMessage({
              id: "shopee.workspace.overview.manage-connections",
            })}
            onAction={() => workspace.setActiveTab("connections")}
          >
            <IntlMessages id="shopee.workspace.overview.channels-title" />
          </PanelHeading>
          <div className="shopee-overview__channel-list">
            {salesChannels.length ? (
              salesChannels.map((channel) => (
                <div
                  className="shopee-overview__channel-row"
                  key={`${channel.name}-${channel.handle}`}
                >
                  <span className={`shopee-overview__channel-logo shopee-overview__channel-logo--${channel.type.toLowerCase()}`}>
                    {channel.type}
                  </span>
                  <span className="shopee-overview__channel-name">
                    <strong>{channel.name}</strong>
                    <small>{channel.handle}</small>
                  </span>
                  <span
                    className={`shopee-overview__status shopee-overview__status--${
                      channel.state === "active" ? "success" : "danger"
                    }`}
                  >
                    {channel.state === "active" ? (
                      <IntlMessages id="shopee.workspace.overview.channel-active" />
                    ) : (
                      <IntlMessages id="shopee.workspace.overview.channel-error" />
                    )}
                  </span>
                  <small className="shopee-overview__channel-orders">
                    <IntlMessages id="shopee.workspace.overview.new-orders-colon" />{" "}
                    <strong className="text-dark">{channel.orders}</strong>
                  </small>
                </div>
              ))
            ) : (
              <div className="text-muted text-center py-4">
                <i className="simple-icon-share d-block mb-2" style={{ fontSize: "24px", opacity: 0.5 }} />
                <IntlMessages id="shopee.workspace.overview.no-shops" />
              </div>
            )}
          </div>
          <button
            type="button"
            className="shopee-overview__connect-button transition-all"
            onClick={() => workspace.setActiveTab("connections")}
          >
            <i className="simple-icon-plus mr-1" />{" "}
            <IntlMessages id="shopee.workspace.overview.connect-more" />
          </button>
        </section>

        {/* Sync Activity Timeline Widget */}
        <section className="shopee-overview__panel shopee-overview__activity">
          <PanelHeading
            action={intl.formatMessage({ id: "shopee.workspace.overview.view-logs" })}
            onAction={() => workspace.setActiveTab("logs")}
          >
            <IntlMessages id="shopee.workspace.overview.activity-title" />
          </PanelHeading>
          <div className="shopee-overview__timeline">
            {recentActivity.length ? (
              recentActivity.map((activity, index) => (
                <div
                  className={`shopee-overview__timeline-row shopee-overview__timeline-row--${activity.tone}`}
                  key={`${activity.label}-${activity.time}-${index}`}
                >
                  <span className="shopee-overview__timeline-marker">
                    <i
                      className={
                        activity.tone === "success"
                          ? "simple-icon-check"
                          : activity.tone === "danger"
                            ? "simple-icon-close"
                            : "simple-icon-info"
                      }
                    />
                  </span>
                  <div>
                    <strong className="text-dark">{activity.label}</strong>
                    <span>{activity.detail}</span>
                    <small>{activity.time}</small>
                  </div>
                  <div className="shopee-overview__timeline-result">
                    <span>{activity.result}</span>
                    <small>{activity.meta}</small>
                  </div>
                  {index < recentActivity.length - 1 ? (
                    <span className="shopee-overview__timeline-line" />
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-muted text-center py-4">
                <i className="simple-icon-clock d-block mb-2" style={{ fontSize: "24px", opacity: 0.5 }} />
                <IntlMessages id="shopee.workspace.overview.no-activity" />
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Analytics Grid: Top Selling Products & Revenue Channels */}
      <div className="shopee-overview__analytics-grid">
        {/* Top Selling Products */}
        <section className="shopee-overview__panel shopee-overview__products">
          <PanelHeading>
            <IntlMessages id="shopee.workspace.overview.top-products" />{" "}
            <small className="text-muted font-weight-normal">
              (<IntlMessages id="shopee.workspace.overview.top-products-note" />)
            </small>{" "}
            <i className="simple-icon-info text-muted" />
          </PanelHeading>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="shopee-overview__period text-muted font-weight-bold" style={{ fontSize: "11px" }}>
              <IntlMessages id="shopee.workspace.overview.top-period" />{" "}
              <i className="simple-icon-arrow-down ml-1" />
            </span>
            <Badge color="light" pill className="text-muted">
              Top 3
            </Badge>
          </div>

          {topProducts.length ? (
            topProducts.map(({ product, sold }, index) => {
              return (
                <div
                  className="shopee-overview__product-row p-2 rounded mb-2 transition-all"
                  key={product.id || product.platform_item_id || index}
                  style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                >
                  <span
                    className={`shopee-overview__product-rank ${
                      index === 0 ? "rank-1" : index === 1 ? "rank-2" : "rank-3"
                    }`}
                  >
                    #{index + 1}
                  </span>
                  <span className="shopee-overview__product-image">
                    <i className="iconsminds-box-full text-primary" />
                  </span>
                  <span className="shopee-overview__product-name">
                      <strong className="text-dark">
                        {product.platform_name ||
                          product.vmass_product_name || (
                            <IntlMessages id="shopee.workspace.overview.product-unknown" />
                          )}
                      </strong>
                      <small className="text-muted">{product.platform_sku || "—"}</small>
                  </span>
                  <span className="shopee-overview__product-bar">
                    <i
                      style={{
                        width: `${Math.max(25, 100 - index * 25)}%`,
                        background: index === 0 ? "#EE4D2D" : index === 1 ? "#3b82f6" : "#10b981",
                      }}
                    />
                  </span>
                  <strong className="shopee-overview__product-sales text-dark">
                    <IntlMessages
                      id="shopee.workspace.overview.sold-count"
                      values={{ count: sold }}
                    />
                  </strong>
                </div>
              );
            })
          ) : (
            <div className="text-muted text-center py-4">
              <i className="iconsminds-box-full d-block mb-2" style={{ fontSize: "24px", opacity: 0.5 }} />
              <IntlMessages id="shopee.workspace.overview.no-products" />
            </div>
          )}
        </section>

        {/* Revenue Breakdown */}
        <section className="shopee-overview__panel shopee-overview__revenue">
          <PanelHeading>
            <IntlMessages id="shopee.workspace.overview.revenue-title" />{" "}
            <i className="simple-icon-info text-muted" />
          </PanelHeading>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="shopee-overview__period text-muted font-weight-bold" style={{ fontSize: "11px" }}>
              <IntlMessages id="shopee.workspace.overview.revenue-period" />{" "}
              <i className="simple-icon-arrow-down ml-1" />
            </span>
          </div>

          <div className="shopee-overview__revenue-body">
            <div
              className="shopee-overview__donut"
              role="img"
              aria-label={intl.formatMessage(
                { id: "shopee.workspace.overview.revenue-total-aria" },
                { amount: money(gmv) },
              )}
            >
              <div>
                <strong className="text-dark" style={{ fontSize: "16px" }}>{money(gmv)} đ</strong>
                <span className="text-muted">
                  <IntlMessages id="shopee.workspace.overview.total-revenue" />
                </span>
              </div>
            </div>
            <div className="shopee-overview__legend">
              {revenueChannels.length ? (
                revenueChannels.map((channel) => (
                  <div key={channel.label} className="d-flex align-items-center justify-content-between p-2 rounded mb-1" style={{ background: "#f8fafc" }}>
                    <div className="d-flex align-items-center">
                      <span className={`shopee-overview__legend-dot shopee-overview__legend-dot--${channel.tone} mr-2`} />
                      <span className="text-dark font-weight-semibold" style={{ fontSize: "12px" }}>{channel.label}</span>
                    </div>
                    <strong className="text-dark" style={{ fontSize: "12px" }}>
                      {money(channel.amount)} đ{" "}
                      <small className="text-muted">({channel.percent}%)</small>
                    </strong>
                  </div>
                ))
              ) : (
                <div className="d-flex align-items-center justify-content-between p-2 rounded" style={{ background: "#f8fafc" }}>
                  <div className="d-flex align-items-center">
                    <span className="shopee-overview__legend-dot shopee-overview__legend-dot--blue mr-2" />
                    <span className="text-dark font-weight-semibold" style={{ fontSize: "12px" }}>Shopee Official Store</span>
                  </div>
                  <strong className="text-dark" style={{ fontSize: "12px" }}>
                    {money(gmv)} đ <small className="text-muted">(100%)</small>
                  </strong>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
});
